import { Trans, t } from '@lingui/macro';
import { Rating, TextField } from '@mui/material';
import { URLKeys, noddiAsync } from 'noddi-async';
import { NoddiButton, NoddiDummyButton, noddiColors } from 'noddi-ui-common';
import { NoddiBasicCard, useIsWithinTailWindBreakpoint } from 'noddi-ui-web';
import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../../components/ApiErrorMessageWithTrans';
import { CopyReferralCodeCard } from '../PaymentInfo/CopyReferralCodeCard';

function RatingComponent({
  rating,
  setRating,
  label,
  showDivider = true
}: {
  rating: number | null;
  setRating: (value: number | null) => void;
  label: string;
  showDivider?: boolean;
}) {
  const isLg = useIsWithinTailWindBreakpoint('lg');
  return (
    <div className='flex flex-col gap-1'>
      <span> {label}</span>
      <Rating
        style={{ color: noddiColors.purple }}
        size={isLg ? 'medium' : 'large'}
        value={rating}
        onChange={(_, newValue) => {
          if (newValue) {
            setRating(newValue);
          }
        }}
      />
      {showDivider && <div className='mt-2 h-0.25 bg-outlineStroke' />}
    </div>
  );
}

export const FeedbackAfterCompletedBooking = ({
  bookingId,
  referralCode
}: {
  bookingId: number;
  referralCode: string;
}) => {
  const [feedbackComment, setFeedbackComment] = useState<string | null>('');
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [politenessRating, setPolitenessRating] = useState<number | null>(null);
  const [easeOfUseRating, setEaseOfUseRating] = useState<number | null>(null);
  const [communicationRating, setCommunicationRating] = useState<number | null>(null);
  const [carRating, setCarRating] = useState<number | null>(null);

  const [showFeedbackContent, setShowFeedbackContent] = useState(false);

  const {
    mutateAsync: addFeedback,
    isPending: isAddFeedbackPending,
    isSuccess: isAddFeedbackSuccess,
    error: addFeedbackError
  } = noddiAsync.usePost({
    type: URLKeys.postFeedback
  });

  const isDisabled = !overallRating || !easeOfUseRating || !communicationRating || !carRating;

  if (!showFeedbackContent) {
    return (
      <div className='mt-6 flex w-full flex-col gap-6 xxs:mt-6 xxs:gap-8 lg:mt-16 lg:flex-row lg:gap-16'>
        <NoddiBasicCard
          onClick={() => setShowFeedbackContent(true)}
          className='flex w-full flex-col gap-4 text-darkPurple'
        >
          <h3 className='font-bold text-5'>
            <Trans>How was your experience?</Trans>
          </h3>
          <span>
            <Trans>Help us improve by sharing your thoughts</Trans>
          </span>

          <div className='flex h-full items-end'>
            <NoddiDummyButton className='w-full'>
              <Trans>Give feedback</Trans>
            </NoddiDummyButton>
          </div>
        </NoddiBasicCard>
        <CopyReferralCodeCard referralCode={referralCode} />
      </div>
    );
  }

  return (
    <>
      {isAddFeedbackSuccess ? (
        <div className='flex h-[50vh] flex-col gap-4'>
          <p className='font-bold text-5 text-darkPurple'>
            <Trans>Thank you for your feedback!</Trans> 🎊
          </p>
          <CopyReferralCodeCard referralCode={referralCode} />
        </div>
      ) : (
        <div>
          <NoddiButton
            startIcon='ArrowLeft'
            onPress={() => {
              setShowFeedbackContent(false);
            }}
            variant='ghost'
            className='-ml-4 -mt-4'
          >
            <Trans>Back</Trans>
          </NoddiButton>
          <NoddiBasicCard className='flex flex-col gap-2'>
            <RatingComponent
              rating={easeOfUseRating}
              setRating={(value) => {
                setEaseOfUseRating(value);
              }}
              label={t`How easy did you find it to book the service?`}
            />
            <RatingComponent
              rating={communicationRating}
              setRating={(value) => {
                setCommunicationRating(value);
              }}
              label={t`How would you rate our communication before the appointment?`}
            />
            <RatingComponent
              rating={carRating}
              setRating={(value) => {
                setCarRating(value);
              }}
              label={t`How satisfied are you with the result of the job?`}
            />
            <RatingComponent
              rating={politenessRating}
              setRating={(value) => {
                setPolitenessRating(value);
              }}
              label={t`How polite did you find the technician to be? (optional)`}
            />
            <RatingComponent
              rating={overallRating}
              setRating={(value) => {
                setOverallRating(value);
              }}
              label={t`How likely are you to recommend Noddi to someone else?`}
              showDivider={false}
            />
            <div className='mt-2 flex flex-col gap-2'>
              <div className='flex flex-col gap-1'>
                <p>{t`Other?`}</p>
                <TextField
                  multiline
                  fullWidth
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  label={t`Please share your experience with us`}
                />
              </div>
              {addFeedbackError && <ApiErrorMessageWithTrans error={addFeedbackError} />}
              <NoddiButton
                loading={isAddFeedbackPending}
                className='w-full'
                disabled={isDisabled}
                onPress={() => {
                  addFeedback({
                    customerComment: feedbackComment,
                    customerRatingCarResult: carRating!,
                    customerRatingCommunication: communicationRating!,
                    customerRatingOverall: overallRating!,
                    customerRatingPoliteness: politenessRating,
                    customerRatingEaseOfUse: easeOfUseRating!,
                    bookingId
                  });
                }}
              >
                <Trans>Send feedback</Trans>
              </NoddiButton>
            </div>
          </NoddiBasicCard>
        </div>
      )}
    </>
  );
};
